import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  redirect,
} from "@tanstack/react-router";
import { PortalDemoGate, RootScreen, TenantGate } from "./App";
import { validarBuscaDePrazos } from "./rotas/busca-de-prazos";
import {
  validarBuscaDeContasAPagar,
  validarBuscaDeContasAReceber,
  validarBuscaDePecas,
  validarBuscaDePericias,
  validarBuscaDeProcessos,
  validarBuscaDePropostas,
} from "./rotas/buscas-das-listas";
import { periodoDaBusca } from "./lib/periodo-de-datas";
import { recorteDaEntregaDaBusca } from "./lib/recorte-da-entrega";
import { parseAppSearch, stringifyAppSearch } from "./lib/search-params";
import { processTabFromParam } from "./pages/processo/process-tabs";
import {
  idsDaBusca,
  tiposDaAgenda,
  visaoDaAgenda,
} from "./lib/agenda-views";
import { TelaDeErroDaRota } from "./components/ErrorBoundary";
import { focoDaUrl, type ChaveDeFoco } from "./lib/foco";
import { validarBuscaDoAgendamento } from "./rotas/busca-do-agendamento";
import { entregaDoLinkDePrazos, validarBuscaDaListaDoAgendamento } from "./rotas/busca-da-lista-do-agendamento";
import { isRecorteDaElaboracao, isSelecaoDaElaboracao } from "./lib/etapas";
import {
  LISTA_DA_SECAO,
  SECAO_DE_USUARIOS_ANTIGA,
  buscaOrdenavel,
  ligado,
  lista,
  migrarBuscaAntigaDeConversas,
  modalidadeDeViagem,
  seHouver,
  texto,
  tipoDeAssistente,
  tipoDePessoaFiltro,
  vinculoFiltro,
  portalFiltro,
  registroFiltro,
  validarBuscaDeConversas,
} from "./rotas/buscas";
import { etapasDaBusca } from "./lib/recorte-de-laudos";
import type {
  AdvogadosSearch,
  AgendaSearch,
  ConfiguracoesComListaSearch,
  PerfilSearch,
  ConversasSearch,
  DocumentosSearch,
  ElaboracaoSearch,
  FinanceiroSearch,
  ListaComInativosSearch,
  ListaOrdenavelSearch,
  PeritosDoJuizoSearch,
  PeritosSearch,
  PessoasSearch,
  ProcessoDetalheSearch,
  PropostaAssistenciaSearch,
  ViagensSearch,
  VisaoGeralSearch,
} from "./rotas/buscas";
import { validarBuscaDeDefesas } from "./rotas/busca-de-defesas";
import { validarBuscaDeNormas } from "./rotas/busca-de-normas";
import { validarBuscaDeNotificacoes } from "./rotas/busca-de-notificacoes";
import {
  AcceptInvitationPage,
  AccountsPayablePage,
  AccountsReceivablePage,
  AdvogadosPage,
  AgendaPage,
  AgendamentoListaPage,
  AgendamentoPage,
  AjudaPage,
  CadastrosPage,
  ConfiguracoesPage,
  ConsultoriaPage,
  ConversasComSessao,
  DashboardPage,
  DocumentsPage,
  ElaboracaoPage,
  ExpertisesPage,
  FeedbacksPage,
  DefesasPage,
  FinanceiroPage,
  PainelFinanceiroPage,
  DrePage,
  RentabilidadePage,
  FornecedoresPage,
  ForgotPasswordPage,
  FrotaPage,
  LegalBriefsPage,
  LocadorasPage,
  NormasPage,
  NotificacoesPage,
  PerfilPage,
  PeritosDoJuizoPage,
  PeritosPage,
  PessoasPage,
  PlatformConsolePage,
  ProcessDetailPage,
  ProcessesPage,
  ProposalAssistancePage,
  ProposalConsultingPage,
  ProposalSpecialPage,
  ProposalsPage,
  PublicProposalResponsePage,
  PedirPropostaPage,
  ReportsPage,
  ResetPasswordPage,
  SpecialWorksPage,
  ViagensPage,
  suspense,
} from "./rotas/telas";
import { EntradaDiretaScreen, EntrarScreen } from "./rotas/entrada";

// A árvore de rotas como OBJETO (TUT-177). Antes eram três roteadores
// convivendo: o `<Routes>` do tenant, um funil de despacho por `pathname`
// antes dele (manutenção, `/console`, portal demo, rotas públicas comparadas
// literalmente) e o `?secao=` dentro do Console. Os dois primeiros colapsaram
// aqui — a precedência do funil virou estrutura: raiz (manutenção) → Console |
// [portal demo → rotas públicas | [sessão → escritório]]. O `?secao=` do
// Console CONTINUA query string de propósito: virar rota é escopo da TUT-160,
// e misturar as duas trocas tornaria qualquer regressão inatribuível.
//
// A árvore é declarada em código, não por arquivo: rota por arquivo moveria as
// 29 páginas de lugar e tornaria ilegível justamente o diff onde qualquer
// regressão precisa ser atribuível.

/**
 * O caminho do **Console Brevi** — o painel administrativo do SaaS (TUT-143).
 *
 * Era `/plataforma`, e o dono pediu a troca por um motivo prático: "plataforma"
 * é palavra que o produto usa para o conceito (escopo de plataforma × escopo de
 * escritório) e ficava ruim de referenciar a TELA. O nome interno segue
 * `platform*` no código e no banco de propósito: o conceito continua correto, e
 * renomear a trava de isolamento (`app.platform_context`) por estética seria
 * risco sem retorno.
 */
export const CONSOLE_PATH = "/console";

// ---------------------------------------------------------------------------
// A árvore.
// ---------------------------------------------------------------------------

export const rootRoute = createRootRoute({ component: RootScreen });

// Rota de plataforma (TUT-069): ramo próprio, resolvido FORA do portão de
// sessão do tenant. O console tem login, sessão e cookies próprios, e nunca
// compartilha estado com o cliente. O filho coringa reproduz o `startsWith`
// do funil antigo: qualquer sufixo sob /console continua no Console.
export const consoleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: CONSOLE_PATH,
  component: () => suspense(<PlatformConsolePage />),
});
const consoleTailRoute = createRoute({
  getParentRoute: () => consoleRoute,
  path: "$",
  component: () => null,
});

// O portão do harness demo do portal cobre TUDO que não é Console — inclusive
// as rotas públicas de identidade, como no funil antigo.
export const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: PortalDemoGate,
});

// Rotas públicas de identidade: acessíveis sem sessão, fora do portão
// autenticado (o usuário ainda não tem login). A consulta de sessão nem monta
// nelas — ficam fora do TenantGate na árvore.
const conviteAceitarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/convite/aceitar",
  component: () => suspense(<AcceptInvitationPage />),
});
const entrarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/entrar",
  component: () => suspense(<EntrarScreen />),
});

const portalAdvogadoRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portal/advogado",
  component: () => suspense(<EntradaDiretaScreen entrada="advogado" />),
});
const portalAssistenteRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portal/assistente",
  component: () => suspense(<EntradaDiretaScreen entrada="assistente" />),
});
const senhaEsqueciRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/senha/esqueci",
  component: () => suspense(<ForgotPasswordPage />),
});
const senhaRedefinirRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/senha/redefinir",
  component: () => suspense(<ResetPasswordPage />),
});
const propostaPublicaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/proposta/$token",
  component: () => suspense(<PublicProposalResponsePage />),
});
// TUT-541 — o pedido de proposta pelo site: público, a chave do formulário
// no endereço (sem slug nem id do escritório).
const pedirPropostaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/pedir-proposta/$chave",
  component: () => suspense(<PedirPropostaPage />),
});

// O escritório inteiro mora sob este portão: sessão, login no lugar da rota
// (sem redirecionar — a URL pedida permanece) e a bifurcação EXTERNAL → Portal.
export const tenantRoute = createRoute({
  getParentRoute: () => appRoute,
  id: "tenant",
  component: TenantGate,
});

const visaoGeralRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/",
  component: DashboardPage,
  // TUT-246 — ver `VisaoGeralSearch`.
  validateSearch: (busca: Record<string, unknown>): VisaoGeralSearch => {
    const { modo, foco, processo, ficha, ...resto } = busca;
    return {
      ...resto,
      ...(modo === "detalhada" || modo === "simples" ? { modo } : {}),
      // A mesma regra da pilha do Agendamento (TUT-244): lista fechada
      // descartada aqui e conferida DE NOVO na leitura, e a ficha sem a lista
      // atrás dela é modal órfão.
      ...(focoDaUrl(foco) ? { foco: foco as ChaveDeFoco } : {}),
      ...(focoDaUrl(foco) ? { processo: texto(processo) } : {}),
      // TUT-486 — o item de widget abre a ficha do processo, sem lista atrás.
      ...(texto(ficha) ? { ficha: texto(ficha) } : {}),
    } as VisaoGeralSearch;
  },
});
// TUT-538 (D28) — o Kanban de processos saiu do menu e virou a VISTA Kanban de
// Processos. O endereço antigo continua abrindo, na vista, com o que trazia
// (`?visao=tarefas` inclusive).
const kanbanRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/kanban",
  beforeLoad: ({ search }) => {
    throw redirect({
      to: "/processos",
      search: { ...(search as Record<string, unknown>), vista: "kanban" },
      replace: true,
    });
  },
});
// TUT-338 — Atendimento e Chat numa tela só (pedido do dono em 27/09). O
// endereço antigo abre a aba Atendimento, com a conversa que trazia.
const atendimentoRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/atendimento",
  beforeLoad: ({ search }) => {
    throw redirect({
      to: "/conversas",
      search: { ...(search as Record<string, unknown>), aba: "atendimento" },
    });
  },
});
const conversasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/conversas",
  // TUT-491 — a caixa única (`?c=`, `?canal=`, `?recorte=`, `?q=`); o link das
  // duas abas antigas (`?aba=`, `?conversa=`) redireciona sem quebrar.
  validateSearch: (busca: Record<string, unknown>): ConversasSearch => validarBuscaDeConversas(busca),
  beforeLoad: ({ search }) => {
    const migrada = migrarBuscaAntigaDeConversas(search);
    if (migrada) throw redirect({ to: "/conversas", search: migrada, replace: true });
  },
  component: ConversasComSessao,
});
const processosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/processos",
  component: ProcessesPage,
  // TUT-177 (TUT-497: o corpo mora em `rotas/buscas-das-listas.ts`).
  validateSearch: validarBuscaDeProcessos,
});
const processoDetalheRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/processos/$processId",
  component: ProcessDetailPage,
  // `?aba=` tipado (TUT-177), preservando `process-tabs.ts` como registro:
  // slug desconhecido ou aposentado resolve para uma área existente já na
  // validação — a página só reescreve a URL quando o slug está obsoleto.
  validateSearch: (busca: Record<string, unknown>): ProcessoDetalheSearch => {
    const { aba, peca, laudo, ...resto } = busca;
    return {
      ...resto,
      aba: typeof aba === "string" ? processTabFromParam(aba) : undefined,
      peca: texto(peca),
      laudo: texto(laudo),
    } as ProcessoDetalheSearch;
  },
});
const periciasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/pericias",
  component: ExpertisesPage,
  // TUT-181 (TUT-497: o corpo mora em `rotas/buscas-das-listas.ts`).
  validateSearch: validarBuscaDePericias,
});
// TUT-568 — a tela de Prazos saiu (decisão do dono, 30/10): o endereço antigo
// abre a lista do Agendamento, e a faixa do link salvo vira o recorte da entrega.
const prazosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/prazos",
  validateSearch: validarBuscaDePrazos,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/agendamento-lista", search: entregaDoLinkDePrazos(search), replace: true });
  },
});
// TUT-230/TUT-243 — a segunda tela de etapa: o resumo e o quadro do
// Agendamento. O item existe no rascunho dela separado de Processos; a lista
// de perícias continua em `/pericias`.
const agendamentoRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/agendamento",
  component: AgendamentoPage,
  // TUT-530 (revisão): o corpo mora em `rotas/busca-do-agendamento.ts`.
  validateSearch: validarBuscaDoAgendamento,
});

// TUT-568 — a vista Lista do Agendamento: os itens do Kanban em tabela, mais
// as recusadas, com os filtros na URL. O alternador a liga a `/agendamento`.
const agendamentoListaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/agendamento-lista",
  component: AgendamentoListaPage,
  validateSearch: validarBuscaDaListaDoAgendamento,
});

// TUT-230 — a tela de etapa. Sem `validateSearch`: o recorte por coluna é
// estado local do quadro, não endereço; a lista com filtros endereçáveis
// continua sendo `/elaboracao-lista` (era `/pecas` até a TUT-566).
const elaboracaoRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/elaboracao",
  component: ElaboracaoPage,
  // TUT-249 — a tela não tinha `validateSearch` NENHUM: atividade, recorte e
  // filtros viviam em `useState`. Valor fora da lista fechada é descartado
  // aqui, e não repassado à tela: a URL é entrada do usuário, e um recorte
  // inventado tem de virar "sem recorte" em vez de uma fila vazia sem motivo.
  //
  // O PADRÃO é a ausência da chave, como em `page` e `inativos`: `?atividade=todos`
  // seria estado escrito dizendo "o padrão", e a primeira tela que esquecesse
  // de apagá-lo passaria a anunciar uma escolha que ninguém fez.
  validateSearch: (busca: Record<string, unknown>): ElaboracaoSearch => {
    const {
      atividade,
      meus,
      recorte,
      atribuido,
      cliente,
      reclamada,
      etapa,
      revisor,
      periodoDe,
      periodoAte,
      entrega,
      entregaDe,
      entregaAte,
      ...resto
    } = busca;
    // TUT-313 — o link antigo `?recorte=meus` (de antes de "meus" virar
    // escopo) continua abrindo o que abria: vira `meus`, sem recorte.
    // `ligado`: a URL chega como TEXTO (`parseAppSearch`), então o que a
    // própria tela escreve (`meus: true`) volta como `"true"`.
    const meusLigado = ligado(meus) === true || recorte === "meus";
    return {
      ...resto,
      atividade:
        typeof atividade === "string" &&
        atividade !== "todos" &&
        isSelecaoDaElaboracao(atividade)
          ? atividade
          : undefined,
      meus: meusLigado ? true : undefined,
      recorte:
        typeof recorte === "string" && isRecorteDaElaboracao(recorte)
          ? recorte
          : undefined,
      atribuido: texto(atribuido),
      cliente: texto(cliente),
      reclamada: texto(reclamada),
      // TUT-480 — o recorte de laudos: lista fechada (a chave repete) e dias
      // que existem; o período invertido cai INTEIRO (a API o recusa com 400).
      etapa: seHouver(etapasDaBusca(etapa)),
      revisor: texto(revisor),
      ...periodoDaBusca({ periodoDe, periodoAte }),
      // TUT-578 — o recorte da entrega dos painéis de prazo.
      ...recorteDaEntregaDaBusca({ entrega, entregaDe, entregaAte }),
    } as ElaboracaoSearch;
  },
});

// TUT-566 (30/10) — a lista de peças é a vista Lista da Elaboração, e o
// endereço acompanha o nome: `/pecas` virou `/elaboracao-lista`. O alternador
// da Elaboração a alcança; ela não é item do menu.
const elaboracaoListaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/elaboracao-lista",
  component: LegalBriefsPage,
  // TUT-497: o corpo mora em `rotas/buscas-das-listas.ts`.
  validateSearch: validarBuscaDePecas,
});
// O endereço antigo segue abrindo (favorito, link enviado, atalho do
// Agendamento), com a mesma busca. Mesmo `validateSearch` da rota nova: o
// `search` já sai tipado como `PecasSearch`.
const pecasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/pecas",
  validateSearch: validarBuscaDePecas,
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/elaboracao-lista", search, replace: true });
  },
});
const consultoriaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/consultoria",
  component: ConsultoriaPage,
});
const trabalhosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/trabalhos",
  // TUT-204c: a ordenação entra no schema tipado da rota, como `page` já
  // estava. Link com a ordem escolhida passa a ser compartilhável.
  validateSearch: (busca: Record<string, unknown>): ListaOrdenavelSearch =>
    buscaOrdenavel("specialWorks", busca),
  component: SpecialWorksPage,
});
const propostasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/propostas",
  // TUT-343 — a pilha dos quatro contadores de propostas (veio de Clientes)
  // entra POR CIMA de `buscaOrdenavel`, no mesmo padrão do Agendamento:
  // `foco`/`processo` saem do `busca` ANTES, para não vazar crus pelo
  // `...resto` do helper.
  // TUT-584 — o corpo mora em `rotas/buscas-das-listas.ts`, junto das outras
  // listas com visões salvas (a visão aplica a busca pela MESMA validação).
  validateSearch: validarBuscaDePropostas,
  component: ProposalsPage,
});
const propostaAssistenciaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/propostas/assistencia",
  validateSearch: (
    busca: Record<string, unknown>,
  ): PropostaAssistenciaSearch => {
    const { id, ...resto } = busca;
    return { ...resto, id: texto(id) } as PropostaAssistenciaSearch;
  },
  component: ProposalAssistancePage,
});
const propostaConsultoriaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/propostas/consultoria",
  validateSearch: (
    busca: Record<string, unknown>,
  ): PropostaAssistenciaSearch => {
    const { id, ...resto } = busca;
    return { ...resto, id: texto(id) } as PropostaAssistenciaSearch;
  },
  component: ProposalConsultingPage,
});
const propostaEspecialRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/propostas/especial",
  validateSearch: (
    busca: Record<string, unknown>,
  ): PropostaAssistenciaSearch => {
    const { id, ...resto } = busca;
    return { ...resto, id: texto(id) } as PropostaAssistenciaSearch;
  },
  component: ProposalSpecialPage,
});
const agendaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/agenda",
  component: AgendaPage,
  // TUT-245 — ver `AgendaSearch`. `...resto` preserva o que não é desta tela
  // (o harness `?__tutelae_portal=1`), e cada chave declarada sai daqui só
  // validada — nunca também crua, que é o caso que a guarda cobra.
  validateSearch: (busca: Record<string, unknown>): AgendaSearch => {
    const {
      visao,
      tipo,
      assunto,
      atribuido,
      cliente,
      local,
      de,
      ate,
      compromisso,
      prazos,
      ...resto
    } = busca;
    return {
      ...resto,
      visao: visaoDaAgenda(visao),
      // TUT-342 — listas: a chave repetida na URL, e ausente quando vazia.
      tipo: seHouver(tiposDaAgenda(tipo)),
      assunto: seHouver(idsDaBusca(assunto)),
      atribuido: seHouver(idsDaBusca(atribuido)),
      cliente: seHouver(idsDaBusca(cliente)),
      local: texto(local),
      de: texto(de),
      ate: texto(ate),
      compromisso: texto(compromisso),
      prazos: ligado(prazos),
    } as AgendaSearch;
  },
});
const documentosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/documentos",
  // TUT-204c: a ordenação entra no schema tipado da rota, como `page` já
  // estava. A tela não tem tabela — o controle é o seletor "Ordenar por" —,
  // mas o contrato da URL é o mesmo das listas com cabeçalho.
  validateSearch: (busca: Record<string, unknown>): DocumentosSearch =>
    // TUT-204i — a classe e o prazo de guarda entram TIPADOS. A lista fechada
    // é o catálogo do cadastro, e quem a confere é a TELA: valor inventado na
    // URL mostra a lista inteira, não uma lista vazia sem motivo.
    // TUT-489a — os filtros por coluna viraram listas (`lista` aceita o valor
    // único antigo), e a prévia aberta viaja em `documento`.
    buscaOrdenavel("documents", busca, {
      classe: lista,
      retencao: lista,
      processos: lista,
      status: lista,
      sigilo: lista,
      etiqueta: lista,
      periodo: texto,
      documento: texto,
    }),
  component: DocumentsPage,
});
const relatoriosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/relatorios",
  component: ReportsPage,
});
// TUT-251 — o Financeiro ganhou tela PRÓPRIA. Até aqui o grupo do menu não
// tinha destino e caía no primeiro filho; agora ele abre os dois quadros, e as
// duas listas continuam sendo os filhos, que é onde os gestos moram.
const financeiroRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/financeiro",
  // TUT-276 — a pilha dos quatro contadores do topo, no mesmo padrão do
  // Agendamento: `focoDaUrl` decide, `processo` só viaja COM foco.
  validateSearch: (busca: Record<string, unknown>): FinanceiroSearch => {
    const { foco, processo, ...resto } = busca;
    return {
      ...resto,
      ...(focoDaUrl(foco) ? { foco: foco as ChaveDeFoco } : {}),
      ...(focoDaUrl(foco) ? { processo: texto(processo) } : {}),
    } as FinanceiroSearch;
  },
  component: FinanceiroPage,
});
// TUT-588b/c/d — Painel financeiro, DRE e Rentabilidade (V3: itens próprios do grupo Financeiro).
const painelFinanceiroRoute = createRoute({ getParentRoute: () => tenantRoute, path: "/financeiro/painel", component: PainelFinanceiroPage });
const dreRoute = createRoute({ getParentRoute: () => tenantRoute, path: "/financeiro/dre", component: DrePage });
const rentabilidadeRoute = createRoute({ getParentRoute: () => tenantRoute, path: "/financeiro/rentabilidade", component: RentabilidadePage });
const contasAPagarRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/financeiro/contas-a-pagar",
  // TUT-334 e TUT-483 (TUT-497: o corpo mora em `rotas/buscas-das-listas.ts`).
  validateSearch: validarBuscaDeContasAPagar,
  component: AccountsPayablePage,
});
const contasAReceberRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/financeiro/contas-a-receber",
  // TUT-483 (TUT-497: o corpo mora em `rotas/buscas-das-listas.ts`).
  validateSearch: validarBuscaDeContasAReceber,
  component: AccountsReceivablePage,
});
// TUT-330 — Transportes deixou de ter página (o grupo só abre e fecha, dono
// em 27/09). O endereço antigo abre Viagens, o diário do grupo.
const transportesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/transportes",
  beforeLoad: () => {
    throw redirect({ to: "/transportes/viagens" });
  },
});
const transportesFrotaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/transportes/frota",
  component: FrotaPage,
});
const transportesViagensRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/transportes/viagens",
  // TUT-204c — ver /trabalhos: mesma regra, mesmo helper.
  validateSearch: (busca: Record<string, unknown>): ViagensSearch =>
    // TUT-204g: a modalidade NASCE na URL — ela vivia em `useState` e já era
    // aplicada no SERVIDOR, então o filtro sumia no F5 e o link mostrava outra
    // lista. A lista fechada é o `TRIP_MODES`; "TODAS" é a ausência da chave.
    // TUT-204h — `veiculo` entra pelo mesmo portão: o identificador é opaco
    // (não há lista fechada a conferir aqui), então quem descarta o veículo
    // que não existe é a TELA, que conhece a frota do escritório.
    buscaOrdenavel("vehicleTrips", busca, {
      mode: modalidadeDeViagem,
      veiculo: texto,
    }),
  component: ViagensPage,
});
// TUT-330 — "Todos os cadastros" saiu (dono, 27/09: "mantém só um"). O
// endereço antigo continua abrindo, agora na lista de Clientes, para não
// quebrar favorito nem link já enviado.
const cadastrosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/cadastros",
  beforeLoad: () => {
    throw redirect({ to: "/organizacoes" });
  },
});
const organizacoesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/organizacoes",
  // TUT-181 fatia 2 — a página entra na URL tipada, como em /processos e
  // /pericias: link compartilhado abre onde apontava. TUT-204c: a ordenação
  // entra no schema tipado da rota, como `page` já estava.
  // TUT-343 — a pilha dos contadores de propostas foi para `/propostas`.
  validateSearch: (busca: Record<string, unknown>): ListaOrdenavelSearch =>
    buscaOrdenavel("organizations", busca),
  component: CadastrosPage,
});
const pessoasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/pessoas",
  // TUT-181 fatia 2b — a página entra na URL tipada, como em /documentos e
  // /processos: link compartilhado abre onde apontava.
  validateSearch: (busca: Record<string, unknown>): PessoasSearch =>
    // TUT-204c: a ordenação entra no schema tipado da rota, como `page` já
    // estava. Link com a ordem escolhida passa a ser compartilhável.
    // TUT-204g: e o filtro de papel, que viajava cru, entra com ela.
    buscaOrdenavel("people", busca, { papel: texto }),
  component: PessoasPage,
});
const peritosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  // TUT-289: o cadastro de peritos virou o de assistentes — a URL segue a
  // renomeação, e quem tinha /peritos salvo é redirecionado abaixo.
  path: "/assistentes",
  // TUT-181 fatia 2b — a página entra na URL tipada, como em /documentos e
  // /processos: link compartilhado abre onde apontava.
  validateSearch: (busca: Record<string, unknown>): PeritosSearch =>
    // TUT-204c: a ordenação entra no schema tipado da rota, como `page` já
    // estava. Link com a ordem escolhida passa a ser compartilhável.
    // TUT-204g: `empresa` e `inativos` deixam de viajar crus.
    // TUT-289: `tipo` filtra assistente x perito do juízo, também na URL.
    buscaOrdenavel("experts", busca, {
      empresa: texto,
      inativos: ligado,
      tipo: tipoDeAssistente,
      // TUT-580: as colunas novas, filtráveis (e compartilháveis pelo endereço).
      pessoa: tipoDePessoaFiltro,
      vinculo: vinculoFiltro,
      portal: portalFiltro,
    }),
  // TUT-536 (D10): o perito do juízo saiu desta tela. Quem tinha
  // `?tipo=perito-do-juizo` salvo (favorito, link enviado) chega na tela nova,
  // com a busca e o "mostrar desativados" que trazia.
  beforeLoad: ({ search }) => {
    if (search.tipo === "perito-do-juizo") {
      throw redirect({
        to: "/peritos-do-juizo",
        search: {
          ...(search.q ? { q: search.q } : {}),
          ...(search.inativos ? { inativos: true as const } : {}),
        },
        replace: true,
      });
    }
  },
  component: PeritosPage,
});
// TUT-536 (D10): os peritos do juízo em tela própria, com cadastro único por
// registro. O item do menu é filho de Processos (é onde a perícia os usa), mas o
// ENDEREÇO fica fora de `/processos/`: esse prefixo é só do detalhe do processo
// (a e2e e os atalhos tratam todo `/processos/<x>` como um processo). A rota é estática: o
// roteador a prefere a `/processos/$processId`.
const peritosDoJuizoRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/peritos-do-juizo",
  validateSearch: (busca: Record<string, unknown>): PeritosDoJuizoSearch =>
    buscaOrdenavel("experts", busca, { inativos: ligado, registro: registroFiltro }),
  component: PeritosDoJuizoPage,
});
// TUT-289: quem tinha /peritos salvo (favorito, link antigo) cai em
// /assistentes preservando a busca da URL.
const peritosRedirectRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/peritos",
  // Mesmo validateSearch de `/assistentes`: sem ele `search: (s) => s` abaixo
  // não teria como saber que `s` é `PeritosSearch`, e o TypeScript recusa o
  // repasse.
  validateSearch: (busca: Record<string, unknown>): PeritosSearch =>
    buscaOrdenavel("experts", busca, {
      empresa: texto,
      inativos: ligado,
      tipo: tipoDeAssistente,
    }),
  // `search` já sai validado como `PeritosSearch` (mesmo validateSearch de
  // `/assistentes` acima) — repassar o objeto, e não uma função redutora,
  // mantém o tipo exato que a rota de destino espera.
  beforeLoad: ({ search }) => {
    throw redirect({ to: "/assistentes", search });
  },
});
const advogadosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/advogados",
  // TUT-181 fatia 2b — a página entra na URL tipada, como em /documentos e
  // /processos: link compartilhado abre onde apontava.
  validateSearch: (busca: Record<string, unknown>): AdvogadosSearch =>
    // TUT-204c: a ordenação entra no schema tipado da rota, como `page` já
    // estava. Link com a ordem escolhida passa a ser compartilhável.
    // TUT-204g: `escritorio` e `inativos` deixam de viajar crus.
    buscaOrdenavel("counsels", busca, {
      escritorio: texto,
      inativos: ligado,
      // TUT-580: Tipo de pessoa e Portal.
      pessoa: tipoDePessoaFiltro,
      portal: portalFiltro,
    }),
  component: AdvogadosPage,
});
// TUT-538 (M23) — "pode ser só uma tabelinha": Assuntos saiu do grupo Clientes
// do menu e virou a tabela dentro da tela de Clientes. O endereço antigo abre
// lá, na âncora da tabela. A busca, a página e a ordem da tela antiga não vão
// junto: a URL de `/organizacoes` é da lista de clientes, e a tabela de
// assuntos guarda o próprio recorte na tela.
const assuntosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/assuntos",
  beforeLoad: () => {
    throw redirect({ to: "/organizacoes", hash: "assuntos", replace: true });
  },
});
const locadorasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/locadoras",
  // TUT-181 fatia 2b — a página entra na URL tipada, como em /documentos e
  // /processos: link compartilhado abre onde apontava.
  validateSearch: (busca: Record<string, unknown>): ListaComInativosSearch =>
    // TUT-204c: a ordenação entra no schema tipado da rota, como `page` já
    // estava. Link com a ordem escolhida passa a ser compartilhável.
    // TUT-204g: `inativos` NASCE aqui — na tela ele vivia em `useState`, então
    // recarregar perdia o filtro e o link não mostrava o que a pessoa via.
    buscaOrdenavel("rentalCompanies", busca, { inativos: ligado }),
  component: LocadorasPage,
});
// TUT-589 — fornecedores: quem presta serviço ou vende ao escritório. Mesma
// URL tipada das locadoras: a ordem, a página e o "mostrar desativados" vão na
// URL, e o link compartilhado abre a mesma lista.
const fornecedoresRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/fornecedores",
  validateSearch: (busca: Record<string, unknown>): ListaComInativosSearch =>
    buscaOrdenavel("suppliers", busca, { inativos: ligado }),
  component: FornecedoresPage,
});
// TUT-336 — Usuários e o Registro de atividade viraram seções das
// Configurações (decisão do dono em 27–28/09). O endereço antigo continua
// abrindo, na seção equivalente e com a busca, a página e a ordem que trazia.
const usuariosRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/usuarios",
  beforeLoad: ({ search }) => {
    const { secao, ...resto } = search as Record<string, unknown>;
    throw redirect({
      to: "/configuracoes",
      search: {
        ...resto,
        secao: SECAO_DE_USUARIOS_ANTIGA[String(secao)] ?? "usuarios",
      },
    });
  },
});
const atividadeRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/atividade",
  beforeLoad: ({ search }) => {
    throw redirect({
      to: "/configuracoes",
      search: { ...(search as Record<string, unknown>), secao: "atividade" },
    });
  },
});
// TUT-517 — `/notificacoes` é a central da PESSOA (o sino em página inteira:
// lista e detalhe). Desde a TUT-337 o endereço só redirecionava para a Central
// do escritório, que continua em Configurações › Notificações; nada no app
// apontava mais para ele (`app-shell-contract.test.ts`).
const notificacoesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/notificacoes",
  validateSearch: validarBuscaDeNotificacoes,
  component: NotificacoesPage,
});
const perfilRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/perfil",
  validateSearch: (busca: Record<string, unknown>): PerfilSearch => {
    const { secao, "confirmar-email": confirmar, ...resto } = busca;
    return {
      ...resto,
      secao: texto(secao),
      ...(texto(confirmar) ? { "confirmar-email": texto(confirmar) } : {}),
    } as PerfilSearch;
  },
  component: PerfilPage,
});
const configuracoesRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/configuracoes",
  // TUT-283 — `secao=` é só STRING aqui: o catálogo de seções (quem alcança
  // qual) depende da SESSÃO, e este validador não tem acesso a ela — só ao
  // objeto de busca. A conferência "é um id do catálogo E está visível nesta
  // sessão" acontece no PONTO DE LEITURA, dentro de `ConfiguracoesPage`, que é
  // a mesma regra que `useSearch() da rota FILHA` documenta em AGENTS.md.
  //
  // TUT-336 — a seção com lista (Usuários, Registro de atividade) valida a
  // busca, a página e a ordem pela lista DELA; as outras não carregam nenhuma.
  validateSearch: (
    busca: Record<string, unknown>,
  ): ConfiguracoesComListaSearch => {
    const secao = texto(busca.secao);
    const listaDaSecao = secao ? LISTA_DA_SECAO[secao] : undefined;
    if (listaDaSecao)
      return buscaOrdenavel(listaDaSecao, busca, {
        secao: texto,
        ...(secao === "notificacoes" ? { aba: texto, status: lista } : {}),
      });
    const {
      secao: _secao,
      q: _q,
      page: _page,
      sort: _sort,
      dir: _dir,
      aba: _aba,
      status: _status,
      ...resto
    } = busca;
    return { ...resto, secao } as ConfiguracoesComListaSearch;
  },
  component: ConfiguracoesPage,
});
// TUT-537 — Defesas, a biblioteca por documento (D36): só leitura, paginada e
// ordenável no servidor, com cada filtro na URL.
const defesasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/defesas",
  validateSearch: validarBuscaDeDefesas,
  component: DefesasPage,
});
// TUT-505 — a consulta normativa do escritório (só leitura); o atalho da
// perícia e do laudo chega com `?norma=` ou `?catalogo=`.
const normasRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/normas",
  validateSearch: validarBuscaDeNormas,
  component: NormasPage,
});
const ajudaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/ajuda",
  component: AjudaPage,
});
const feedbacksRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "/feedbacks",
  component: FeedbacksPage,
});
// O coringa do escritório: caminho desconhecido volta para a Visão geral —
// depois de passar pelo portão de sessão, como antes (quem chega deslogado em
// /caminho-inexistente vê o login, com a URL preservada).
const foraDoMapaRoute = createRoute({
  getParentRoute: () => tenantRoute,
  path: "$",
  component: () => <Navigate to="/" replace />,
});

export const routeTree = rootRoute.addChildren([
  consoleRoute.addChildren([consoleTailRoute]),
  appRoute.addChildren([
    entrarRoute,
    portalAdvogadoRoute,
    portalAssistenteRoute,
    conviteAceitarRoute,
    senhaEsqueciRoute,
    senhaRedefinirRoute,
    propostaPublicaRoute,
    pedirPropostaRoute,
    tenantRoute.addChildren([
      visaoGeralRoute,
      kanbanRoute,
      atendimentoRoute,
      conversasRoute,
      processosRoute,
      processoDetalheRoute,
      periciasRoute,
      prazosRoute,
      agendamentoRoute,
      agendamentoListaRoute,
      elaboracaoRoute,
      defesasRoute,
      elaboracaoListaRoute,
      pecasRoute,
      consultoriaRoute,
      trabalhosRoute,
      propostasRoute,
      propostaAssistenciaRoute,
      propostaConsultoriaRoute,
      propostaEspecialRoute,
      agendaRoute,
      documentosRoute,
      relatoriosRoute,
      financeiroRoute,
      painelFinanceiroRoute,
      dreRoute,
      rentabilidadeRoute,
      contasAPagarRoute,
      contasAReceberRoute,
      transportesRoute,
      transportesFrotaRoute,
      transportesViagensRoute,
      cadastrosRoute,
      organizacoesRoute,
      pessoasRoute,
      peritosRoute,
      peritosDoJuizoRoute,
      peritosRedirectRoute,
      advogadosRoute,
      assuntosRoute,
      locadorasRoute,
      fornecedoresRoute,
      usuariosRoute,
      atividadeRoute,
      notificacoesRoute,
      perfilRoute,
      configuracoesRoute,
      normasRoute,
      ajudaRoute,
      feedbacksRoute,
      foraDoMapaRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  // TUT-392 — sem isto, erro de render em qualquer rota mostrava o padrão do
  // TanStack ("Something went wrong!", em inglês) no lugar da tela inteira, e
  // o `ErrorBoundary` da casa (fora do roteador) nunca era alcançado.
  defaultErrorComponent: TelaDeErroDaRota,
  // O formato da URL é contrato de produto — ver `lib/search-params.ts`.
  parseSearch: parseAppSearch,
  stringifySearch: stringifyAppSearch,
});

// Registra o roteador no sistema de tipos: é o que torna `to` tipado em todo
// `<Link>`/`navigate` — link para rota que não existe vira erro de
// `pnpm typecheck`, que é a guarda que o `grep` nunca foi.
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
